import { useSelector } from 'react-redux';
import { Routes, Route, Navigate, useParams } from 'react-router';

import BusinessHour from '@/components/pages/BusinessHours/BusinessHour';
import BusinessHours from '@/components/pages/BusinessHours/BusinessHours';
import DeskSettings from '@/components/pages/Desk/DeskSettings';
import Desks from '@/components/pages/Desks/Desks';
import EnvironmentRouter from '@/components/pages/Desks/EnvironmentRouter';
import IntegrationRouter from '@/components/pages/Integrations/IntegrationRouter';
import NotFound from '@/components/pages/NotFound/NotFound';
import DeskIdProvider from '@/components/templates/DeskIdProvider';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import useDesks from '@/hooks/useDesks';
import Bundle from '@/modules/bundles/pages/Bundle/Bundle';
import Bundles from '@/modules/bundles/pages/Bundles/Bundles';
import Departments from '@/modules/departments/pages/Departments';
import RulesRouter from '@/modules/rules/pages/Rules/RulesRouter';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

const DesksRouter: React.FC = () => {
  const slug = useSelector(selectAccountSlug);
  const { listStatus } = useDesks();
  const { slug: urlSlug } = useParams();
  const canRead = useSelector((state: RootState) => getPermissions(state, 'desks', actions.READ));

  if (slug !== urlSlug || (canRead && listStatus !== 'success')) {
    return null;
  }

  return (
    <>
      <PermissionsWrapper module="desks">
        <Routes>
          <Route path="/" element={<Desks />} />
          <Route path="/:deskId/*" element={<DeskIdProvider />}>
            <Route element={<EnvironmentRouter />}>
              <Route index element={<Navigate to="business_hours" replace />} />
              <Route path="business_hours" element={<BusinessHours />} />
              <Route path="departments/*" element={<Departments />} />
              <Route path="context_bundles" element={<Bundles />} />
              <Route path="settings" element={<DeskSettings />} />
            </Route>
            <Route path="integrations/*" element={<IntegrationRouter />} />
            <Route path="rules/*" element={<RulesRouter />} />
            <Route path="context_bundles/:bundleId" element={<Bundle />} />
            <Route path="business_hours/:businessHourId" element={<BusinessHour />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </PermissionsWrapper>
    </>
  );
};
DesksRouter.displayName = 'DesksRouter';

export default DesksRouter;
